import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Search,
  Wand2,
  LayoutTemplate,
  PenLine,
  ChevronDown,
  ChevronUp,
  Building2,
  MapPin,
  Calendar,
  User,
  Download,
  FileImage,
  AlignLeft,
  BarChart2,
  Info,
  Paperclip,
  ExternalLink,
  GitCompare,
  Plus,
  Minus,
  ArrowRightLeft,
} from "lucide-react";
import { generateQuotePDF } from "@/lib/pdf-generator";
import { readPdfTemplateSnapshot } from "@workspace/api-zod/pdf-template";
import { useToast } from "@/hooks/use-toast";

type AdminLineItem = {
  name: string;
  description: string | null;
  quantity: number;
  unit: string;
  unitCost: string;
  labourCost: string | null;
  materialCost: string | null;
  plantCost: string | null;
  subtotal: string;
  category: string;
  section: string;
  isOptional: boolean;
};

type AdminProfile = {
  companyName: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  vatNumber: string | null;
  logoUrl: string | null;
  market: string;
};

type SnapshotLineItem = {
  name: string;
  category: string;
  description: string | null;
  quantity: string;
  unit: string;
  unitCost: string;
  section: string;
};

type DiffStatus = "added" | "removed" | "modified" | "unchanged";

type DiffRow = {
  status: DiffStatus;
  name: string;
  category: string;
  origQty: string | null;
  origUnitCost: string | null;
  origDescription: string | null;
  currQty: string | null;
  currUnitCost: string | null;
  currDescription: string | null;
  changedFields: string[];
};

function computeQuoteDiff(
  snapshot: SnapshotLineItem[],
  current: AdminLineItem[],
): DiffRow[] {
  const fmt = (s: string) => s.toLowerCase().trim();
  const snapMap = new Map<string, SnapshotLineItem>();
  for (const item of snapshot) {
    snapMap.set(fmt(item.name), item);
  }
  const currMap = new Map<string, AdminLineItem>();
  for (const item of current) {
    currMap.set(fmt(item.name), item);
  }

  const rows: DiffRow[] = [];

  for (const snap of snapshot) {
    const key = fmt(snap.name);
    const curr = currMap.get(key);
    if (!curr) {
      rows.push({
        status: "removed",
        name: snap.name,
        category: snap.category,
        origQty: snap.quantity,
        origUnitCost: snap.unitCost,
        origDescription: snap.description,
        currQty: null,
        currUnitCost: null,
        currDescription: null,
        changedFields: [],
      });
    } else {
      const changedFields: string[] = [];
      const qtyChanged = Math.abs(parseFloat(snap.quantity) - curr.quantity) > 0.001;
      const costChanged = Math.abs(parseFloat(snap.unitCost) - parseFloat(curr.unitCost)) > 0.01;
      const descChanged = (snap.description ?? "") !== (curr.description ?? "");
      if (qtyChanged) changedFields.push("qty");
      if (costChanged) changedFields.push("cost");
      if (descChanged) changedFields.push("description");
      rows.push({
        status: changedFields.length > 0 ? "modified" : "unchanged",
        name: snap.name,
        category: snap.category,
        origQty: snap.quantity,
        origUnitCost: snap.unitCost,
        origDescription: snap.description,
        currQty: String(curr.quantity),
        currUnitCost: curr.unitCost,
        currDescription: curr.description,
        changedFields,
      });
    }
  }

  for (const curr of current) {
    const key = fmt(curr.name);
    if (!snapMap.has(key)) {
      rows.push({
        status: "added",
        name: curr.name,
        category: curr.category,
        origQty: null,
        origUnitCost: null,
        origDescription: null,
        currQty: String(curr.quantity),
        currUnitCost: curr.unitCost,
        currDescription: curr.description,
        changedFields: [],
      });
    }
  }

  return rows;
}

type AdminQuote = {
  pdfTemplate?: unknown;
  id: string;
  quoteNumber: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  companyName: string | null;
  status: string;
  quoteType: string;
  quoteClass: string | null;
  projectType: string | null;
  projectAddress: string | null;
  description: string | null;
  subtotal: string;
  overheadAmount: string;
  profitAmount: string;
  contingencyAmount: string;
  totalPrice: string;
  vatRate: string;
  vatAmount: string;
  grandTotal: string;
  includeVat: boolean | null;
  validUntil: string | null;
  assumptions: string | null;
  exclusions: string | null;
  termsConditions: string | null;
  paymentTerms: string | null;
  projectTimeline: string | null;
  commencementNote: string | null;
  reviewerNotes: string[];
  uploadedFileNames: string[];
  uploadedFileKeys: string[];
  lineItemCount: number;
  estimatedDays: number | null;
  confidenceLevel: string | null;
  createdAt: string;
  profile: AdminProfile | null;
  lineItems: AdminLineItem[];
  originalLineItemsSnapshot: SnapshotLineItem[] | null;
};

const STATUS_COLOURS: Record<string, string> = {
  draft: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  confirmed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  sent: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  accepted: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

const CONFIDENCE_COLOURS: Record<string, string> = {
  high: "text-green-600 dark:text-green-400",
  medium: "text-yellow-600 dark:text-yellow-400",
  low: "text-red-600 dark:text-red-400",
};

function TypeIcon({ type }: { type: string }) {
  if (type === "auto") return <Wand2 className="h-3.5 w-3.5 text-primary" />;
  if (type === "template") return <LayoutTemplate className="h-3.5 w-3.5 text-orange-500" />;
  return <PenLine className="h-3.5 w-3.5 text-muted-foreground" />;
}

function typeLabel(t: string) {
  if (t === "auto") return "Smart";
  if (t === "template") return "Template";
  if (t === "manual") return "Manual";
  return t;
}

function fmtMoney(v: string | number | null | undefined) {
  const n = typeof v === "string" ? parseFloat(v) : (v ?? 0);
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function QuoteDetailModal({ quote, onClose }: { quote: AdminQuote; onClose: () => void }) {
  const { toast } = useToast();
  const [descExpanded, setDescExpanded] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const descPreviewLength = 200;
  const descIsLong = (quote.description?.length ?? 0) > descPreviewLength;
  const descDisplay = descExpanded || !descIsLong
    ? quote.description
    : quote.description?.slice(0, descPreviewLength) + "...";

  async function handleDownloadPDF() {
    setPdfLoading(true);
    try {
      await generateQuotePDF(
        {
          pdfTemplate: readPdfTemplateSnapshot(quote.pdfTemplate),
          quoteNumber: quote.quoteNumber,
          status: quote.status,
          projectAddress: quote.projectAddress,
          projectType: quote.projectType,
          description: quote.description,
          subtotal: quote.subtotal,
          overheadAmount: quote.overheadAmount,
          profitAmount: quote.profitAmount,
          contingencyAmount: quote.contingencyAmount,
          totalPrice: quote.totalPrice,
          vatRate: quote.vatRate,
          vatAmount: quote.vatAmount,
          grandTotal: quote.grandTotal,
          includeVat: quote.includeVat,
          showCostBreakdown: false,
          showItemDescriptions: true,
          showQuantityUnits: true,
          validUntil: quote.validUntil,
          assumptions: quote.assumptions,
          exclusions: quote.exclusions,
          termsConditions: quote.termsConditions ?? quote.profile?.companyName ?? null,
          paymentTerms: quote.paymentTerms,
          projectTimeline: quote.projectTimeline,
          commencementNote: quote.commencementNote,
          createdAt: quote.createdAt,
          confidenceLevel: quote.confidenceLevel,
          lineItems: quote.lineItems.map(item => ({
            section: item.section,
            category: item.category,
            name: item.name,
            description: item.description,
            quantity: String(item.quantity),
            unit: item.unit,
            unitCost: item.unitCost,
            labourCost: item.labourCost,
            materialCost: item.materialCost,
            plantCost: item.plantCost,
            subtotal: item.subtotal,
            isOptional: item.isOptional,
          })),
          client: null,
        },
        quote.profile ? {
          name: quote.profile.companyName,
          address: quote.profile.address ?? undefined,
          phone: quote.profile.phone ?? undefined,
          email: quote.profile.email ?? undefined,
          website: quote.profile.website ?? undefined,
          vatNumber: quote.profile.vatNumber ?? undefined,
          logoUrl: quote.profile.logoUrl ?? undefined,
          market: quote.profile.market,
        } : undefined,
      );
    } catch {
      toast({ title: "PDF generation failed", variant: "destructive" });
    } finally {
      setPdfLoading(false);
    }
  }

  const hasUploadedFiles = quote.uploadedFileNames?.length > 0;

  const generationMethod = (() => {
    if (quote.quoteType === "template") return { label: "Template Quote", icon: <LayoutTemplate className="h-4 w-4 text-orange-500" />, detail: "Built from a saved template" };
    if (quote.quoteType === "manual") return { label: "Manual Quote", icon: <PenLine className="h-4 w-4 text-muted-foreground" />, detail: "Items added manually" };
    if (hasUploadedFiles) {
      return {
        label: "Smart Quote (AI) — with drawings",
        icon: <Wand2 className="h-4 w-4 text-primary" />,
        detail: `${quote.uploadedFileNames.length} file${quote.uploadedFileNames.length !== 1 ? "s" : ""} uploaded`,
      };
    }
    return {
      label: "Smart Quote (AI) — description only",
      icon: <Wand2 className="h-4 w-4 text-primary" />,
      detail: "No drawings or photos were uploaded",
    };
  })();

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <TypeIcon type={quote.quoteType} />
              <span>{quote.quoteNumber}</span>
              <Badge className={`text-xs ml-1 ${STATUS_COLOURS[quote.status] ?? ""}`}>{quote.status}</Badge>
              {quote.quoteClass === "estimate" && (
                <Badge variant="outline" className="text-xs">Estimate</Badge>
              )}
            </DialogTitle>
            <Button
              size="sm"
              variant="outline"
              disabled={pdfLoading}
              onClick={handleDownloadPDF}
              className="gap-1.5 shrink-0"
            >
              <Download className="h-3.5 w-3.5" />
              {pdfLoading ? "Generating..." : "Download PDF"}
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* User + project info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <User className="h-3.5 w-3.5" />
                <span className="font-medium text-foreground">{quote.userName || "Unknown"}</span>
              </div>
              {quote.companyName && <p className="text-muted-foreground pl-5">{quote.companyName}</p>}
              {quote.userEmail && <p className="text-muted-foreground pl-5">{quote.userEmail}</p>}
            </div>
            <div className="space-y-1.5">
              {quote.projectAddress && (
                <div className="flex items-start gap-1.5 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>{quote.projectAddress}</span>
                </div>
              )}
              {quote.projectType && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Building2 className="h-3.5 w-3.5" />
                  <span className="capitalize">{quote.projectType.replace(/_/g, " ")}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>{fmtDate(quote.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Generation method panel */}
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5" />
              How this quote was generated
            </p>
            <div className="flex items-center gap-2 text-sm">
              {generationMethod.icon}
              <span className="font-medium">{generationMethod.label}</span>
              <span className="text-muted-foreground">&mdash; {generationMethod.detail}</span>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              {quote.confidenceLevel && (
                <span className="flex items-center gap-1">
                  <BarChart2 className="h-3 w-3" />
                  Confidence:
                  <span className={`font-medium capitalize ${CONFIDENCE_COLOURS[quote.confidenceLevel] ?? ""}`}>
                    {quote.confidenceLevel}
                  </span>
                </span>
              )}
              {quote.estimatedDays != null && (
                <span>Duration: <span className="font-medium text-foreground">{quote.estimatedDays} day{quote.estimatedDays !== 1 ? "s" : ""}</span></span>
              )}
              {quote.lineItemCount > 0 && (
                <span>Items: <span className="font-medium text-foreground">{quote.lineItemCount}</span></span>
              )}
            </div>
            {hasUploadedFiles && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Paperclip className="h-3 w-3" />
                  Uploaded files:
                </p>
                <div className="flex flex-wrap gap-2">
                  {quote.uploadedFileNames.map((name, i) => (
                    <a
                      key={i}
                      href={`/api/admin/quotes/${quote.id}/files/${i}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs bg-background border rounded px-2 py-1 text-primary hover:bg-primary/5 transition-colors"
                    >
                      <FileImage className="h-3 w-3 shrink-0" />
                      <span className="max-w-[180px] truncate">{name}</span>
                      <ExternalLink className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            {quote.reviewerNotes?.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-muted-foreground">AI reviewer notes:</p>
                <ul className="space-y-0.5">
                  {quote.reviewerNotes.map((note, i) => (
                    <li key={i} className="text-xs text-muted-foreground bg-background rounded px-2 py-1 border">{note}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Collapsible description */}
          {quote.description && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <AlignLeft className="h-3.5 w-3.5" />
                  Project Description
                </p>
                {descIsLong && (
                  <button
                    onClick={() => setDescExpanded(e => !e)}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    {descExpanded ? (
                      <><ChevronUp className="h-3 w-3" />Collapse</>
                    ) : (
                      <><ChevronDown className="h-3 w-3" />Expand</>
                    )}
                  </button>
                )}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed bg-muted rounded-lg p-3 whitespace-pre-wrap">
                {descDisplay}
              </p>
            </div>
          )}

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Line Items ({quote.lineItemCount})
              </p>
              <p className="text-sm font-semibold">{fmtMoney(quote.grandTotal)}</p>
            </div>
            {quote.lineItems?.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted">
                      <TableHead className="text-xs">Item</TableHead>
                      <TableHead className="text-xs text-right">Qty</TableHead>
                      <TableHead className="text-xs text-right">Unit Cost</TableHead>
                      <TableHead className="text-xs text-right">Subtotal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quote.lineItems.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs py-2">
                          <p className="font-medium">{item.name}</p>
                          <p className="text-muted-foreground capitalize">{item.category?.replace(/_/g, " ")}</p>
                        </TableCell>
                        <TableCell className="text-xs text-right py-2">{item.quantity} {item.unit}</TableCell>
                        <TableCell className="text-xs text-right py-2">{fmtMoney(item.unitCost)}</TableCell>
                        <TableCell className="text-xs text-right py-2 font-medium">{fmtMoney(item.subtotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No line items stored.</p>
            )}
          </div>

          {/* Builder changes diff — auto quotes with a snapshot only */}
          {quote.quoteType === "auto" && quote.originalLineItemsSnapshot && quote.originalLineItemsSnapshot.length > 0 && (() => {
            const diffRows = computeQuoteDiff(
              quote.originalLineItemsSnapshot as SnapshotLineItem[],
              quote.lineItems,
            );
            const added = diffRows.filter(r => r.status === "added").length;
            const removed = diffRows.filter(r => r.status === "removed").length;
            const modified = diffRows.filter(r => r.status === "modified").length;
            const unchanged = diffRows.filter(r => r.status === "unchanged").length;
            const hasChanges = added + removed + modified > 0;
            return (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                    <GitCompare className="h-3.5 w-3.5" />
                    Builder Changes (vs AI Draft)
                  </p>
                  <div className="flex items-center gap-2 text-xs">
                    {added > 0 && <span className="flex items-center gap-0.5 text-green-600 font-medium"><Plus className="h-3 w-3" />{added} added</span>}
                    {removed > 0 && <span className="flex items-center gap-0.5 text-red-600 font-medium"><Minus className="h-3 w-3" />{removed} removed</span>}
                    {modified > 0 && <span className="flex items-center gap-0.5 text-amber-600 font-medium"><ArrowRightLeft className="h-3 w-3" />{modified} changed</span>}
                    {!hasChanges && <span className="text-muted-foreground">No changes — saved as generated</span>}
                  </div>
                </div>
                {hasChanges && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted">
                          <TableHead className="text-xs w-5"></TableHead>
                          <TableHead className="text-xs">Item</TableHead>
                          <TableHead className="text-xs text-right">AI Qty</TableHead>
                          <TableHead className="text-xs text-right">Final Qty</TableHead>
                          <TableHead className="text-xs text-right">AI Cost</TableHead>
                          <TableHead className="text-xs text-right">Final Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {diffRows.filter(r => r.status !== "unchanged").map((row, i) => {
                          const rowClass =
                            row.status === "added" ? "bg-green-50 dark:bg-green-950/30" :
                            row.status === "removed" ? "bg-red-50 dark:bg-red-950/30" :
                            "bg-amber-50 dark:bg-amber-950/30";
                          const icon =
                            row.status === "added" ? <Plus className="h-3 w-3 text-green-600" /> :
                            row.status === "removed" ? <Minus className="h-3 w-3 text-red-600" /> :
                            <ArrowRightLeft className="h-3 w-3 text-amber-600" />;
                          return (
                            <TableRow key={i} className={rowClass}>
                              <TableCell className="py-2 pl-3 pr-0">{icon}</TableCell>
                              <TableCell className="text-xs py-2">
                                <p className="font-medium">{row.name}</p>
                                <p className="text-muted-foreground capitalize">{row.category?.replace(/_/g, " ")}</p>
                                {row.status === "modified" && row.changedFields.includes("description") && (
                                  <p className="text-amber-600 text-xs mt-0.5">description changed</p>
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-right py-2 text-muted-foreground">
                                {row.origQty != null ? row.origQty : "—"}
                              </TableCell>
                              <TableCell className={`text-xs text-right py-2 font-medium ${row.status === "added" ? "text-green-700 dark:text-green-400" : row.changedFields.includes("qty") ? "text-amber-700 dark:text-amber-400" : ""}`}>
                                {row.currQty != null ? row.currQty : "—"}
                              </TableCell>
                              <TableCell className="text-xs text-right py-2 text-muted-foreground">
                                {row.origUnitCost != null ? fmtMoney(row.origUnitCost) : "—"}
                              </TableCell>
                              <TableCell className={`text-xs text-right py-2 font-medium ${row.status === "added" ? "text-green-700 dark:text-green-400" : row.changedFields.includes("cost") ? "text-amber-700 dark:text-amber-400" : ""}`}>
                                {row.currUnitCost != null ? fmtMoney(row.currUnitCost) : "—"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <div className="px-3 py-2 bg-muted/50 border-t text-xs text-muted-foreground">
                      {unchanged} item{unchanged !== 1 ? "s" : ""} unchanged
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminQuotesPage() {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState<AdminQuote | null>(null);
  const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");

  const { data: allQuotes = [], isLoading } = useQuery<AdminQuote[]>({
    queryKey: ["/api/admin/quotes"],
    queryFn: () => fetch("/api/admin/quotes", { credentials: "include" }).then(r => r.json()),
    refetchInterval: 60_000,
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return allQuotes
      .filter(r => {
        if (typeFilter !== "all" && r.quoteType !== typeFilter) return false;
        if (statusFilter !== "all" && r.status !== statusFilter) return false;
        if (q) {
          const hay = [r.quoteNumber, r.userEmail, r.userName, r.companyName, r.projectAddress, r.projectType]
            .filter(Boolean).join(" ").toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const diff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        return sortDir === "desc" ? diff : -diff;
      });
  }, [allQuotes, search, typeFilter, statusFilter, sortDir]);

  const totalValue = useMemo(() =>
    filtered.reduce((sum, q) => sum + (parseFloat(q.grandTotal) || 0), 0),
    [filtered]);

  const stats = useMemo(() => ({
    total: allQuotes.length,
    auto: allQuotes.filter(q => q.quoteType === "auto").length,
    template: allQuotes.filter(q => q.quoteType === "template").length,
    manual: allQuotes.filter(q => q.quoteType === "manual").length,
  }), [allQuotes]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">All Quotes</h1>
        <p className="text-sm text-muted-foreground mt-1">Every quote generated across all users</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total Quotes", value: stats.total, icon: <FileText className="h-4 w-4 text-muted-foreground" /> },
          { label: "Smart Quotes", value: stats.auto, icon: <Wand2 className="h-4 w-4 text-primary" /> },
          { label: "Template Quotes", value: stats.template, icon: <LayoutTemplate className="h-4 w-4 text-orange-500" /> },
          { label: "Manual Quotes", value: stats.manual, icon: <PenLine className="h-4 w-4 text-muted-foreground" /> },
        ].map(s => (
          <Card key={s.label}>
            <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xs font-medium text-muted-foreground">{s.label}</CardTitle>
              {s.icon}
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-2xl font-bold">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by quote number, user, address..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="auto">Smart</SelectItem>
            <SelectItem value="template">Template</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted">
              <TableHead className="text-xs">Quote</TableHead>
              <TableHead className="text-xs">User</TableHead>
              <TableHead className="text-xs">Project</TableHead>
              <TableHead className="text-xs">Type</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs text-right">
                <button
                  className="flex items-center gap-1 ml-auto"
                  onClick={() => setSortDir(d => d === "desc" ? "asc" : "desc")}
                >
                  Total {sortDir === "desc" ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
                </button>
              </TableHead>
              <TableHead className="text-xs">Date</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground text-sm">Loading quotes...</TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground text-sm">No quotes found</TableCell>
              </TableRow>
            ) : (
              <>
                {filtered.map(q => (
                  <TableRow key={q.id} className="hover:bg-muted/40 cursor-pointer" onClick={() => setSelected(q)}>
                    <TableCell className="py-3">
                      <p className="text-xs font-mono font-medium">{q.quoteNumber}</p>
                      <p className="text-xs text-muted-foreground">{q.lineItemCount} items</p>
                    </TableCell>
                    <TableCell className="py-3">
                      <p className="text-xs font-medium">{q.userName || q.userEmail || "—"}</p>
                      {q.companyName && <p className="text-xs text-muted-foreground">{q.companyName}</p>}
                    </TableCell>
                    <TableCell className="py-3">
                      <p className="text-xs capitalize">{q.projectType?.replace(/_/g, " ") || "—"}</p>
                      {q.projectAddress && <p className="text-xs text-muted-foreground truncate max-w-[180px]">{q.projectAddress}</p>}
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="flex items-center gap-1.5">
                        <TypeIcon type={q.quoteType} />
                        <span className="text-xs">{typeLabel(q.quoteType)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge className={`text-xs ${STATUS_COLOURS[q.status] ?? ""}`}>{q.status}</Badge>
                    </TableCell>
                    <TableCell className="py-3 text-right">
                      <span className="text-xs font-medium">{fmtMoney(q.grandTotal)}</span>
                    </TableCell>
                    <TableCell className="py-3">
                      <span className="text-xs text-muted-foreground">{fmtDate(q.createdAt)}</span>
                    </TableCell>
                    <TableCell className="py-3">
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={e => { e.stopPropagation(); setSelected(q); }}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/40">
                  <TableCell colSpan={5} className="py-2 text-xs text-muted-foreground pl-4">
                    {filtered.length} quote{filtered.length !== 1 ? "s" : ""} shown
                  </TableCell>
                  <TableCell className="py-2 text-right text-xs font-semibold pr-4">
                    {fmtMoney(totalValue)}
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {selected && <QuoteDetailModal quote={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
